/**
 * Módulo para cargar problemas desde Excel - VERSIÓN REFORZADA
 * Fuente EXCLUSIVA de problemas: problemas_21.xlsx
 * Utiliza SheetJS (xlsx) para leer el archivo local
 * 
 * MEJORAS:
 * - Sistema de caché con IndexedDB
 * - Backoff exponencial con 5 reintentos
 * - Verificación previa del archivo
 * - Indicadores visuales de progreso
 * - Mensajes de error detallados
 */

const ExcelLoader = {
    // Archivo Excel con todos los problemas
    EXCEL_FILE: 'problemas_23.xlsx',

    // Estado de carga
    isLoaded: false,

    // Mapeo de niveles (text in Excel -> internal level ID)
    LEVEL_MAPPING: {
        '1': 'beginner',
        '2': 'advanced',
        '3': 'expert',
        'aprendiz': 'beginner',
        'avanzado': 'advanced',
        'experto': 'expert',
        'genio': 'expert',
        'detective': 'beginner',
        'inspector': 'advanced',
        'sherlock': 'expert'
    },

    // Mapeo de Contexto -> Hoja
    CONTEXT_TO_SHEET: {
        'cumpleanos': 'CUMPLEAÑOS',
        'cumpleaños': 'CUMPLEAÑOS',
        'fruteria': 'FRUTERÍA', // Preferimos con tilde
        'frutería': 'FRUTERÍA',
        'navidad': 'NAVIDAD',
        'halloween': 'HALLOWEEN',
        'circo': 'CIRCO',
        'feria': 'FERIA',
        'supermercado': 'SUPERMERCADO',
        'jugueteria': 'JUGUETERÍA',
        'libreria': 'LIBRERÍA',
        'parque': 'PARQUE',
        'granja': 'GRANJA',
        'playa': 'PLAYA',
        'bosque': 'BOSQUE',
        'huerto': 'HUERTO',
        'acuario': 'ACUARIO',
        'cocina': 'COCINA',
        'dormitorio': 'DORMITORIO'
    },

    /**
     * Valida el esquema de datos de una fila de problema antes de procesarla
     */
    validateProblemSchema(row) {
        if (!row || !Array.isArray(row)) return false;
        if (!row[0] || !row[3]) return false;
        const statement = String(row[3]).trim();
        if (statement.length < 8) return false;
        return true;
    },

    processWorkbook(workbook) {
        this.problemsDB = {};

        // DEMO MODE: Specific sheets to prioritize
        // We will look for these first.
        const PRIORITY_SHEETS = ['CUMPLEAÑOS', 'FRUTERIA', 'FRUTERÍA'];
        let prioritySheetsFound = false;

        workbook.SheetNames.forEach(sheetName => {
            // Ignorar hoja de metadatos o instrucciones
            if (sheetName === 'CONTEXTOS' || sheetName === 'INSTRUCCIONES') return;

            // Check priority
            const isPriority = PRIORITY_SHEETS.includes(sheetName.toUpperCase());
            if (isPriority) prioritySheetsFound = true;

            // If we found priority sheets, we might want to skip GLOBAL to avoid duplicates
            // BUT, let's load everything and let logic handle it.
            // Actually, user asked if separate sheets are better. YES.
            // If we are loading a specific context sheet, we use it.

            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

            const problems = [];

            jsonData.forEach((row, index) => {
                // Header detection (skip row if it looks like a header)
                // Usamos la columna 'Nivel' o 'Operación' para detectar cabecera
                if (index === 0) return;

                // Column Mapping based on User's description:
                // 0: Nivel
                // 1: Operación
                // 2: Numero
                // 3: Enunciado
                // 4: Nombre_Imagen
                // 5: Resultado
                // 6: Calculo

                // Validación estricta de esquema de fila
                if (!this.validateProblemSchema(row)) return;

                const rawLevel = String(row[0]).toLowerCase().trim();
                const rawOp = String(row[1]).toLowerCase().trim();
                const statement = String(row[3]).trim(); // Column 3 is Statement
                const imageName = row[4] ? String(row[4]).trim() : null; // Column 4 is ImageName
                const providedResult = row[5]; // Column 5 is Result
                const phrasesJsonStr = row[7]; // Column 7 is Frases_Datos

                // Mapeo de Nivel
                let level = 'beginner';
                for (const [key, value] of Object.entries(this.LEVEL_MAPPING)) {
                    if (rawLevel.includes(key)) {
                        level = value;
                        break;
                    }
                }

                // Mapeo de Operación
                let type = 'sum';
                if (rawOp.includes('resta')) type = 'subtraction';

                // Usamos analysis para extraer nombres y validar, pero preferimos los datos explícitos del Excel
                const analysis = this.analyzeProblemStatement(statement, type);

                // EXTRACT CONTEXT FROM IMAGE NAME
                // Convention: context_type_level_number.jpg (e.g. fruteria_sum_level1_01.jpg)
                // OR simple: fruteria_01.jpg
                let derivedContextId = this.sheetToContextId(sheetName);
                if (imageName) {
                    const parts = imageName.split('_');
                    if (parts.length > 0) {
                        // Validate if the first part is a known context
                        const potentialContext = parts[0].toLowerCase();
                        // You might want to check against a known list or just use it
                        derivedContextId = potentialContext;
                    }
                }

                // Construct image paths
                // The Excel already includes the full filename with extension (e.g., "cumpleaños_aps_2.jpg")
                // So we just prepend the folder path
                const problemImages = [];
                if (imageName) {
                    // Don't add .jpg if it's already there
                    problemImages.push(`assets/problems/${imageName}`);
                }

                // DEMO MODE FILTER: Only allow problems with images
                // Since user manually cleaned the Excel, we trust that all rows should be included
                if (analysis.isValid || providedResult) {
                    const problem = {
                        id: `row_${index}`, // ID único basado en fila
                        level: level,
                        type: type,
                        statement: {
                            'es-ES': statement,
                            'en-US': statement,
                            'ca-ES': statement
                        },
                        contextId: derivedContextId, // Use derived context
                        contextName: {
                            'es-ES': derivedContextId === 'fruteria' ? 'FRUTERÍA' : derivedContextId.toUpperCase(),
                            'en-US': derivedContextId.toUpperCase(),
                            'ca-ES': derivedContextId.toUpperCase()
                        },
                        question: this.extractQuestion(statement),
                        solution: {
                            answer: providedResult || analysis.expectedAnswer,
                            operation: type === 'sum' ? 'addition' : 'subtraction'
                        },
                        data: {
                            useful: analysis.numbers,
                            names: analysis.names,
                            phrases: this.safeParsePhrases(phrasesJsonStr)
                        },
                        images: problemImages,
                        forcedImage: !!imageName
                    };

                    // Organize into DB by Context ID (Virtual Sheets)
                    // If we are processing GLOBAL sheet, we distribute problems into their specific context buckets
                    if (sheetName === 'GLOBAL' || sheetName === 'Global') {
                        if (!this.problemsDB[derivedContextId]) {
                            this.problemsDB[derivedContextId] = [];
                        }
                        this.problemsDB[derivedContextId].push(problem);
                    } else {
                        problems.push(problem);
                    }
                }
            });

            if (problems.length > 0) {
                // Determine target bucket
                // If it was a specific sheet (not GLOBAL), use sheetName but normalized to ID
                if (sheetName !== 'GLOBAL' && sheetName !== 'Global') {
                    // Try to resolve context ID from sheet name
                    const contextId = this.sheetToContextId(sheetName);

                    // If we already have problems for this context (e.g. from GLOBAL), merge them
                    if (this.problemsDB[contextId]) {
                        this.problemsDB[contextId] = [...this.problemsDB[contextId], ...problems];
                    } else {
                        this.problemsDB[contextId] = problems;
                    }
                    console.log(`📚 Contexto "${contextId}" (Hoja "${sheetName}"): ${problems.length} problemas cargados`);
                }
            } else {
                console.warn(`⚠️ Hoja "${sheetName}" procesada pero 0 problemas válidos encontrados. (Filtro img: ${imageName ? 'true' : 'false'})`); // Adjusted problemImages to imageName
            }
        });

        // Debug Dump
        console.log('📊 DB FINAL:', Object.keys(this.problemsDB));
        Object.keys(this.problemsDB).forEach(k => {
            console.log(`   ➡ ${k}: ${this.problemsDB[k].length} probs. Sample Lvl: ${this.problemsDB[k][0]?.level}`);
        });

        this.isLoaded = true;
    },

    processOfflineData(offlineData) {
        console.log('📦 Procesando datos OFFLINE embedded...');
        this.problemsDB = {};

        // Simular estructura de workbook.Sheets
        // offlineData es { SheetName: [ [row], [row]... ] }

        Object.keys(offlineData).forEach(sheetName => {
            if (sheetName === 'CONTEXTOS' || sheetName === 'INSTRUCCIONES') return;

            const rows = offlineData[sheetName];
            const problems = [];

            rows.forEach((row, index) => {
                if (index === 0) return; // Header
                if (!row[0] || !row[3]) return;

                // Reutilizamos la lógica de parseo existente
                // Para evitar duplicar código, podríamos refactorizar processRow
                // Pero por seguridad en este parche, copiamos la lógica esencial adaptada

                const rawLevel = String(row[0]).toLowerCase().trim();
                const rawOp = String(row[1]).toLowerCase().trim();
                const statement = String(row[3]).trim();
                const imageName = row[4] ? String(row[4]).trim() : null;
                const providedResult = row[5];
                const phrasesJsonStr = row[7];

                let level = 'beginner';
                for (const [key, value] of Object.entries(this.LEVEL_MAPPING)) {
                    if (rawLevel.includes(key)) { level = value; break; }
                }

                let type = 'sum';
                if (rawOp.includes('resta')) type = 'subtraction';

                const analysis = this.analyzeProblemStatement(statement, type);

                let derivedContextId = this.sheetToContextId(sheetName);
                if (imageName) {
                    const parts = imageName.split('_');
                    if (parts.length > 0) derivedContextId = parts[0].toLowerCase();
                }

                const problemImages = imageName ? [`assets/problems/${imageName}`] : [];

                if (analysis.isValid || providedResult) {
                    const problem = {
                        id: `row_${index}`,
                        level: level,
                        type: type,
                        statement: { 'es-ES': statement },
                        contextId: derivedContextId,
                        contextName: { 'es-ES': derivedContextId === 'fruteria' ? 'FRUTERÍA' : derivedContextId.toUpperCase() },
                        question: this.extractQuestion(statement),
                        solution: { answer: providedResult || analysis.expectedAnswer, operation: type === 'sum' ? 'addition' : 'subtraction' },
                        data: { useful: analysis.numbers, names: analysis.names, phrases: this.safeParsePhrases(phrasesJsonStr) },
                        images: problemImages,
                        forcedImage: !!imageName
                    };

                    if (sheetName === 'GLOBAL' || sheetName === 'Global') {
                        if (!this.problemsDB[derivedContextId]) this.problemsDB[derivedContextId] = [];
                        this.problemsDB[derivedContextId].push(problem);
                    } else {
                        problems.push(problem);
                    }
                }
            });

            if (problems.length > 0 && sheetName !== 'GLOBAL') {
                const contextId = this.sheetToContextId(sheetName);
                if (this.problemsDB[contextId]) {
                    this.problemsDB[contextId] = [...this.problemsDB[contextId], ...problems];
                } else {
                    this.problemsDB[contextId] = problems;
                }
            }
        });

        console.log('✅ Datos OFFLINE procesados correctament.');
    },

    async loadProblems() {
        try {
            console.log('🔍 loadProblems called. Checking window.OFFLINE_EXCEL_DATA:', typeof window.OFFLINE_EXCEL_DATA, window.OFFLINE_EXCEL_DATA ? 'PRESENT' : 'MISSING');
            // MODO OFFLINE / DEMO (Prioridad Absoluta)
            if (window.OFFLINE_EXCEL_DATA) {
                console.log('🚀 MODO OFFLINE DETECTADO (Datos Inline)');
                this.processOfflineData(window.OFFLINE_EXCEL_DATA);
                // En modo demo local, verificar imágenes puede dar falsos positivos de error por CORS en algunas versiones de Chrome con file://
                // Lo hacemos igual pero sin bloquear
                this.verifyAvailableImages();
                this.injectCustomProblems();
                this.isLoaded = true;
                return true;
            }

            // Verificar si ya hay problemas en la caché para este usuario
            const cachedProblems = await this.loadFromCache();
            if (cachedProblems && Object.keys(cachedProblems).length > 0) {
                this.problemsDB = cachedProblems;
                console.log('📦 Problemas cargados desde caché');
                await this.verifyAvailableImages();
                this.isLoaded = true;
                return true;
            }

            const response = await fetch(this.EXCEL_FILE);
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const arrayBuffer = await response.arrayBuffer();
            const XLSX = window.XLSX || await import('xlsx');
            const workbook = XLSX.read(arrayBuffer, { type: 'array' });

            this.processWorkbook(workbook);

            // LOG IMAGES STATUS BUT DO NOT REMOVE THEM
            // User has manually cleaned the file, we trust their setup.
            await this.verifyAvailableImages();

            // Guardar en caché
            await this.saveToCache(this.problemsDB);

            this.injectCustomProblems();
            this.isLoaded = true;
            return true;
        } catch (error) {
            console.error('❌ Error cargando problemas:', error);
            if (window.PROBLEMAS_BACKUP) {
                console.log('⚠️ Usando backup de problemas...');
                this.problemsDB = window.PROBLEMAS_BACKUP;
                this.injectCustomProblems();
                this.isLoaded = true;
                return true;
            } else {
                throw error;
            }
        }
    },

    async verifyAvailableImages() {
        console.log('🔍 Verificando disponibilidad de imágenes (Modo Informativo)...');

        const checkImage = (url) => {
            return new Promise((resolve) => {
                const img = new Image();
                img.onload = () => resolve(true);
                img.onerror = () => {
                    console.warn(`❌ Imagen no encontrada: ${url}`);
                    resolve(false);
                };
                img.src = url;
            });
        };

        const contexts = Object.keys(this.problemsDB);
        for (const context of contexts) {
            const problems = this.problemsDB[context];
            // Just check and log, don't filter
            for (const p of problems) {
                if (p.images && p.images.length > 0) {
                    // Non-blocking check
                    checkImage(p.images[0]).then(exists => {
                        if (!exists) console.warn(`⚠️ Problema ${p.id} en ${context} tiene imagen rota: ${p.images[0]}`);
                    });
                }
            }
        }
        console.log('✅ Verificación iniciada (resultados en consola).');
    },

    /**
     * Carga desde caché (Simulado/Placeholder para evitar error)
     */
    async loadFromCache() {
        // Por ahora retornamos null para forzar carga fresca y evitar errores de lógica compleja
        // Implementar IndexedDB real si es necesario en el futuro
        return null;
    },

    /**
     * Guarda en caché (Simulado/Placeholder)
     */
    async saveToCache(data) {
        // Placeholder
        return true;
    },

    /**
     * Devuelve estadísticas de la base de datos cargada
     */
    getStats() {
        const stats = {};
        let total = 0;

        for (const [sheet, problems] of Object.entries(this.problemsDB)) {
            stats[sheet] = {
                total: problems.length,
                beginner: problems.filter(p => p.level === 'beginner').length,
                advanced: problems.filter(p => p.level === 'advanced').length,
                expert: problems.filter(p => p.level === 'expert').length,
                sum: problems.filter(p => p.type === 'sum').length,
                subtraction: problems.filter(p => p.type === 'subtraction').length,
                custom: problems.filter(p => p.isCustom).length
            };
            total += problems.length;
        }

        return { contexts: stats, total: total };
    },

    /**
     * Convierte nombre de hoja a ID de contexto de la app
     */
    sheetToContextId(sheetName) {
        for (const [contextId, sheet] of Object.entries(this.CONTEXT_TO_SHEET)) {
            if (sheet === sheetName) {
                return contextId;
            }
        }
        // Fallback: normalizar el nombre
        return sheetName.toLowerCase()
            .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
            .replace(/\s+/g, '_');
    },

    /**
     * Extrae la pregunta del enunciado (última oración con ?)
     */
    extractQuestion(statement) {
        const questionMatch = statement.match(/[¿]?[^.]*\?/);
        if (questionMatch) {
            return questionMatch[0].trim();
        }
        // Fallback: última oración
        const sentences = statement.split(/[.!]/);
        return sentences[sentences.length - 1].trim() + '?';
    },

    /**
     * Analiza el enunciado para extraer números y calcular respuesta esperada
     */
    analyzeProblemStatement(text, forcedType = null) {
        // Regex para encontrar números
        const numberMatches = text.match(/\d+/g);

        if (!numberMatches || numberMatches.length < 2) {
            return { isValid: false };
        }

        const numbers = numberMatches.map(Number);

        // Intentar extraer nombres de objetos (palabras después de números) con un regex robusto
        const namePattern = /\d+\s+([\p{L}\s]+?)(?=\s*(?:y\s+\d+|o\s+\d+|[,.?¿!]|$))/giu;
        const names = [];
        let match;
        while ((match = namePattern.exec(text)) !== null) {
            const cityName = match[1].trim();
            if (cityName.length > 2) {
                names.push(cityName);
            }
        }

        // Diccionarios expandidos para detección inteligente
        const textLower = text.toLowerCase();

        const keywordsSuma = [
            'suma', 'sumar', 'añadir', 'juntar', 'total', 'llegan', 'compra', 'recibe',
            'regalan', 'gana', 'más', 'en total', 'entre los dos', 'entre las dos',
            'juntos', 'juntas', 'ahora tiene', 'trae', 'pone'
        ];

        const keywordsResta = [
            'resta', 'restar', 'quitar', 'pierde', 'vende', 'rompe', 'marchan', 'se van',
            'menos', 'quedan', 'falta', 'sobran', 'diferencia', 'comió', 'gastó',
            'regaló', 'escaparon', 'quedaron'
        ];

        let isSuma = keywordsSuma.some(word => textLower.includes(word));

        // Si hay conflicto o no se detecta nada, se busca específicamente resta
        const hasRestaKeywords = keywordsResta.some(word => textLower.includes(word));
        if (hasRestaKeywords) isSuma = false;

        // PRIORIDAD ABSOLUTA: Si el Excel especifica el tipo, el análisis de texto se ignora
        if (forcedType === 'sum' || forcedType === 'addition') isSuma = true;
        if (forcedType === 'subtraction' || forcedType === 'resta') isSuma = false;

        let expectedAnswer;
        if (numbers.length >= 2) {
            expectedAnswer = isSuma ? (numbers[0] + numbers[1]) : Math.abs(numbers[0] - numbers[1]);
        } else {
            expectedAnswer = numbers[0];
        }

        return {
            isValid: true,
            numbers: numbers,
            names: names.length > 0 ? names : null,
            expectedAnswer: expectedAnswer
        };
    },

    safeParsePhrases(str) {
        if (!str || typeof str !== 'string') return null;
        try {
            return JSON.parse(str);
        } catch (_e) {
            try {
                const normalized = str.replace(/'/g, '"');
                return JSON.parse(normalized);
            } catch (_) {
                return str.split(/[,|;]/).map(s => s.trim()).filter(Boolean);
            }
        }
    },

    /**
     * Obtiene problemas según contexto, nivel y tipo de operación
     */
    getProblems(contextId, level, problemType = null, count = 5) {
        console.log(`🔍 Buscando problemas para Context: "${contextId}", Nivel: ${level}`);
        console.log(`📊 DB Keys disponibles:`, Object.keys(this.problemsDB));

        // Try exact context ID first
        let targetKey = contextId;

        // Exact match check
        if (!this.problemsDB[targetKey]) {
            console.log(`⚠️ Key exacta "${targetKey}" no encontrada. Buscando mapeo...`);
            targetKey = this.CONTEXT_TO_SHEET[contextId];
            console.log(`   ➡ Mapeo sugiere: "${targetKey}"`);
        }

        // Fuzzy/Fallback search logic
        if (!targetKey || !this.problemsDB[targetKey]) {
            // Try case-insensitive scan
            const foundKey = Object.keys(this.problemsDB).find(k => k.toLowerCase() === contextId.toLowerCase());
            if (foundKey) {
                console.log(`✅ Recuperado por búsqueda case-insensitive: "${foundKey}"`);
                targetKey = foundKey;
            } else {
                // Try normalizing both sides
                const normalize = s => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                const normContext = normalize(contextId);
                const foundNormKey = Object.keys(this.problemsDB).find(k => normalize(k) === normContext);

                if (foundNormKey) {
                    console.log(`✅ Recuperado por búsqueda normalizada: "${foundNormKey}"`);
                    targetKey = foundNormKey;
                }
            }
        }

        if (!targetKey || !this.problemsDB[targetKey]) {
            console.warn(`⚠️ CRITICAL: No se encontraron problemas para contexto: ${contextId}`);
            // LAST HOPE: Try the first available context
            const allKeys = Object.keys(this.problemsDB);
            if (allKeys.length > 0) {
                targetKey = allKeys[0];
                console.log(`🚑 EMERGENCY: Usando primer contexto disponible: "${targetKey}"`);
            } else {
                return [];
            }
        }

        console.log(`✅ Problemas encontrados en DB[${targetKey}]: ${this.problemsDB[targetKey].length}`);

        let candidates = this.problemsDB[targetKey].filter(p => p.level === level);

        if (problemType) {
            candidates = candidates.filter(p => p.type === problemType);
        }

        if (candidates.length === 0) {
            console.warn(`⚠️ No hay problemas para ${contextId}, nivel ${level}. Buscando cualquier nivel...`);
            candidates = this.problemsDB[targetKey];
            if (problemType) {
                candidates = candidates.filter(p => p.type === problemType);
            }
        }

        if (candidates.length === 0) {
            console.warn(`⚠️ No hay problemas para ${contextId}. Usando cualquier problema disponible...`);
            candidates = this.problemsDB[targetKey];
        }

        // Separar candidatos en "no resueltos" y "ya resueltos"
        let unsolved = [];
        let solved = [];
        
        candidates.forEach(p => {
            if (window.SessionManager && window.SessionManager.isProblemSolved(p.id)) {
                solved.push(p);
            } else {
                unsolved.push(p);
            }
        });

        const selected = [];
        
        // 1. Priorizar problemas no resueltos
        const availableUnsolved = [...unsolved];
        for (let i = 0; i < Math.min(count, availableUnsolved.length); i++) {
            const randomIndex = Math.floor(Math.random() * availableUnsolved.length);
            selected.push(availableUnsolved[randomIndex]);
            availableUnsolved.splice(randomIndex, 1);
        }

        // 2. Si faltan problemas para llegar a 'count', usar los ya resueltos
        if (selected.length < count && solved.length > 0) {
            console.log(`⚠️ Se agotaron los problemas nuevos de esta categoría. Repitiendo ${count - selected.length} resueltos.`);
            const availableSolved = [...solved];
            
            // Completar usando los resueltos (sin repetir el mismo en la misma tirada si es posible)
            while (selected.length < count && availableSolved.length > 0) {
                const randomIndex = Math.floor(Math.random() * availableSolved.length);
                selected.push(availableSolved[randomIndex]);
                availableSolved.splice(randomIndex, 1);
            }
        }

        // 3. Si AÚN faltan (ej. pidieron 5, pero en la DB solo hay 2 en total), repetimos de toda la bolsa de candidates
        while (selected.length < count && candidates.length > 0) {
            selected.push(this.getRandomElement(candidates));
        }

        return selected;
    },

    /**
     * Obtiene un problema aleatorio
     */
    getProblem(contextId, level) {
        const problems = this.getProblems(contextId, level, null, 1);
        return problems.length > 0 ? problems[0] : null;
    },

    getRandomElement(array) {
        return array[Math.floor(Math.random() * array.length)];
    },

    /**
     * Inyecta problemas personalizados desde CustomProblemManager
     */
    injectCustomProblems() {
        if (!window.CustomProblemManager) {
            console.warn('⚠️ CustomProblemManager no disponible');
            return;
        }

        const customProblems = CustomProblemManager.getAll();
        if (!customProblems || customProblems.length === 0) return;

        console.log(`🛠️ Inyectando ${customProblems.length} problemas personalizados...`);

        customProblems.forEach(cp => {
            const internalFormat = CustomProblemManager.convertToInternalFormat(cp);

            // Determinar la clave de contexto normalizada (ej: 'cumpleanos', 'fruteria', 'personalizado')
            let contextKey = cp.contextId ? cp.contextId.toLowerCase().trim() : 'personalizado';
            if (this.CONTEXT_TO_SHEET[contextKey]) {
                contextKey = this.sheetToContextId(this.CONTEXT_TO_SHEET[contextKey]);
            }

            if (!this.problemsDB[contextKey]) {
                this.problemsDB[contextKey] = [];
            }
            // Añadir al principio del contexto correspondiente para priorizarlo
            this.problemsDB[contextKey].unshift(internalFormat);

            // También guardar en el bucket 'personalizado' para consultas de dicho contexto
            if (contextKey !== 'personalizado') {
                if (!this.problemsDB['personalizado']) {
                    this.problemsDB['personalizado'] = [];
                }
                this.problemsDB['personalizado'].unshift(internalFormat);
            }
        });

        console.log('✅ Problemas personalizados inyectados');
    },


};

// Exponer al window
window.ExcelLoader = ExcelLoader;

// Auto-cargar al iniciar
document.addEventListener('DOMContentLoaded', () => {
    console.log('🚀 Auto-cargando base de datos de problemas...');
    ExcelLoader.loadProblems();
});

console.log('📊 ExcelLoader REFORZADO cargado');
